import type {
  MailLogDetailMailLogDetailPageProps,
  MailLogDetailAttachmentLink,
} from '../types/pages'
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  FileText,
  Info,
  Loader2,
  Mail,
  Paperclip,
  UserPlus,
  X,
} from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { useBreadcrumbPageTitle } from '@/components/layout/DashboardBreadcrumbContext'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { ROUTE_BUILDERS, ROUTE_PATHS } from '@/config/routes'
import { getBrowserApiBaseUrl } from '@/config/env'
import { useUserRole } from '@/hooks/useUserRole'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'
import { EMAIL_EXTRACT_RE, EMAIL_RE, MAILTRAP_BASE_URL } from '../constants/mailLogDetail'
import {
  getMailLogDetail,
  linkMailTraceApplicant,
  resendMailLog,
  searchMailTraceApplicants,
} from '../services/reportsService'
import type {
  MailLogDetail,
  MailLogDetailVariant,
  MailTraceApplicantSearchRow,
} from '../types/api'
import { parseReportDate } from '../utils/reportDateDisplay'
import styles from '../styles/mailLogDetail.module.css'

function formatDateTime(value: unknown): string {
  if (value == null || value === '') return '—'
  const d = parseReportDate(value)
  if (!d) return String(value)
  const datePart = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const timePart = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${datePart} • ${timePart}`
}

function formatBytes(value: unknown): string | undefined {
  const bytes = Number(value)
  if (!Number.isFinite(bytes) || bytes <= 0) return undefined
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1)
  return `${Number((bytes / 1024 ** i).toFixed(i === 0 ? 0 : 2))} ${sizes[i]}`
}

function formatApplicantId(value: unknown): string {
  if (value == null || value === '') return '-'
  return String(value).padStart(8, '0')
}

function formatApplicationId(value: unknown): string {
  if (value == null || value === '') return '-'
  return String(value).padStart(6, '0')
}

function formatPhone(value: unknown): string {
  const digits = String(value ?? '').replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return String(value ?? '') || '-'
}

function processHtmlLinks(html: string): string {
  return html.replace(/<a\s+([^>]*)>/gi, (match, attributes: string) => {
    if (/target\s*=/i.test(attributes)) return match
    if (/href\s*=\s*['"]?(mailto:|tel:|#)/i.test(attributes)) return match
    return `<a ${attributes} target="_blank" rel="noopener noreferrer">`
  })
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

/**
 * Normalize messy mail-log address payloads:
 * - comma-separated strings
 * - objects keyed by display name / address
 * - angle-bracket wrappers, `$placeholders`, duplicates
 */
function formatAddressMap(value: unknown): string[] {
  if (value == null || value === '') return []

  const chunks: string[] = []

  const collect = (input: unknown, depth = 0) => {
    if (input == null || depth > 4) return
    if (typeof input === 'string' || typeof input === 'number') {
      chunks.push(String(input))
      return
    }
    if (Array.isArray(input)) {
      for (const item of input) collect(item, depth + 1)
      return
    }
    if (typeof input === 'object') {
      for (const [key, val] of Object.entries(input as Record<string, unknown>)) {
        chunks.push(key)
        collect(val, depth + 1)
      }
    }
  }

  collect(value)

  const found: string[] = []
  const seen = new Set<string>()

  for (const chunk of chunks) {
    const matches = chunk.match(EMAIL_EXTRACT_RE)
    if (matches) {
      for (const raw of matches) {
        const email = raw.trim().toLowerCase()
        if (!email || email.includes('$') || seen.has(email)) continue
        seen.add(email)
        found.push(email)
      }
      continue
    }
    // Single bare token that already looks like an email
    const token = chunk
      .replace(/[<>]/g, '')
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean)
    for (const t of token) {
      if (t.includes('$') || !EMAIL_RE.test(t)) continue
      const email = t.toLowerCase()
      if (seen.has(email)) continue
      seen.add(email)
      found.push(email)
    }
  }

  return found
}

function firstString(...values: unknown[]): string {
  for (const value of values) {
    if (value != null && String(value).trim()) return String(value)
  }
  return ''
}

function formatDeliveryTime(value: unknown): string | null {
  if (value == null || value === '') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return `${n.toFixed(2)} seconds`
}

function deliveryStatusTone(status: string): string {
  const s = status.toLowerCase()
  if (s.includes('fail') || s.includes('reject') || s.includes('bounce')) {
    return styles.statusBadgeFailed
  }
  if (s.includes('deliver') || s.includes('accept') || s.includes('success')) {
    return styles.statusBadgeDelivered
  }
  return styles.statusBadgeNeutral
}

function detailListForVariant(variant: MailLogDetailVariant, detail: MailLogDetail) {
  if (variant === 'legacy') {
    return {
      title: firstString(detail.subject, detail.fromAddress, detail.recipient, 'Mail Detail'),
      from: formatAddressMap(detail.fromAddress),
      to: formatAddressMap(detail.to_address),
      cc: formatAddressMap(detail.cc_address),
      bcc: formatAddressMap(detail.bcc_address),
      replyTo: formatAddressMap(detail.reply_address),
      date: detail.created_at,
      html: String(detail.html ?? ''),
    }
  }
  if (variant === 'imap') {
    return {
      title: firstString(detail.subject, detail.from_address, detail.from, 'IMAP Mail Detail'),
      from: formatAddressMap(detail.from_address ?? detail.from),
      to: formatAddressMap(detail.to),
      cc: formatAddressMap(detail.cc),
      bcc: formatAddressMap(detail.bcc),
      replyTo: [],
      date: detail.received_at ?? detail.created_at,
      html: String(detail.body ?? detail.body_html ?? ''),
    }
  }
  return {
    title: firstString(detail.subject, detail.recipient, detail.to, 'Mailgun Detail'),
    from: formatAddressMap(detail.from ?? detail.from_address),
    to: formatAddressMap(detail.recipients_to ?? detail.recipient ?? detail.to),
    cc: formatAddressMap(detail.recipients_cc),
    bcc: formatAddressMap(detail.recipients_bcc),
    replyTo: formatAddressMap(detail.reply_to),
    date: detail.created_at ?? detail.sent_at,
    html: String(detail.body_html ?? detail.html ?? detail.body ?? ''),
  }
}

function reportPathForVariant(variant: MailLogDetailVariant) {
  if (variant === 'mailgun') return `${ROUTE_PATHS.DASHBOARD_REPORTS}/mailgun-logs`
  if (variant === 'imap') return '/imap-logs'
  return `${ROUTE_PATHS.DASHBOARD_REPORTS}/mail-logs`
}

function detailPathForVariant(variant: MailLogDetailVariant, id: string | number) {
  return `${reportPathForVariant(variant)}/read/${encodeURIComponent(String(id))}`
}

function normalizeAttachment(
  attachment: unknown,
  index: number,
  variant: MailLogDetailVariant,
  mailId: string,
): MailLogDetailAttachmentLink | null {
  const apiBase = getBrowserApiBaseUrl()
  const token = getAuthTokenFromStorage()

  if (typeof attachment === 'string') {
    const cleanPath = attachment.split('?')[0] ?? attachment
    const name = cleanPath.substring(cleanPath.lastIndexOf('/') + 1) || 'Attachment'
    if (/^https?:\/\//i.test(attachment)) return { name, href: attachment }
    if (attachment.startsWith('/var/www/html')) {
      const parts = attachment.split('/')
      const storagePath = parts.slice(-2).join('/')
      return { name, href: `${apiBase}/storage/${storagePath}` }
    }
    return { name, href: `${apiBase}/storage/${attachment}` }
  }

  const record = asRecord(attachment)
  if (!Object.keys(record).length) return null

  const filename = firstString(
    record.filename,
    record.attachment_name,
    record.content_path && record.exension
      ? `${record.content_path}.${record.exension}`
      : record.content_path,
    'Attachment',
  ).split('&signature=')[0]
  const size = formatBytes(record.attachment_size)

  if (variant === 'mailgun' && record.filename && token) {
    return {
      name: filename,
      href: `${apiBase}/api/mailgun_logs/${mailId}?attachment_id=${index}&access_token=${encodeURIComponent(token)}`,
      size,
    }
  }
  if (typeof record.url === 'string') return { name: filename, href: record.url, size }
  if (typeof record.attachment_path === 'string') {
    const path = record.attachment_path
    return {
      name: filename,
      href: /^https?:\/\//i.test(path) ? path : `${MAILTRAP_BASE_URL}${path}`,
      size,
    }
  }
  return null
}

function AddressField({
  label,
  icon,
  badgeClass,
  values,
}: {
  label: string
  icon: ReactNode
  badgeClass: string
  values: string[]
}) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const updatePosition = () => {
    const el = buttonRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const popoverW =
      popoverRef.current?.offsetWidth ||
      Math.min(288, window.innerWidth - 16)
    let left = rect.left
    if (left + popoverW > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - popoverW - 8)
    }
    setCoords({ top: rect.bottom + 6, left })
  }

  useLayoutEffect(() => {
    if (!open) return
    updatePosition()
  }, [open, values])

  useEffect(() => {
    if (!open) return
    updatePosition()
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onReposition = () => updatePosition()
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', onReposition)
    window.addEventListener('scroll', onReposition, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)
    }
  }, [open])

  if (!values.length) return null
  const primary = values[0]!
  const extra = values.length - 1
  const title = `${label} recipients`

  return (
    <div className={styles.infoRow}>
      <span className={cn(styles.iconBadge, badgeClass)} aria-hidden>
        {icon}
      </span>
      <div className={styles.infoContent}>
        <p className={styles.infoLabel}>{label}</p>
        <div className={styles.recipientLine}>
          <p className={styles.infoValue}>{primary}</p>
          {extra > 0 ? (
            <div className={styles.moreWrap}>
              <button
                ref={buttonRef}
                type="button"
                className={styles.moreBadge}
                onClick={() => {
                  if (open) {
                    setOpen(false)
                    return
                  }
                  updatePosition()
                  setOpen(true)
                }}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label={`Show ${extra} more ${label.toLowerCase()} recipients`}
              >
                +{extra} more
                <ChevronDown className="h-3.5 w-3.5" aria-hidden />
              </button>
              {open && coords
                ? createPortal(
                    <div
                      ref={popoverRef}
                      className={styles.popover}
                      role="dialog"
                      aria-label={title}
                      style={{ top: coords.top, left: coords.left }}
                    >
                      <div className={styles.popoverHeader}>
                        <h4 className={styles.popoverTitle}>
                          {title} ({values.length})
                        </h4>
                        <button
                          type="button"
                          className={styles.popoverClose}
                          onClick={() => setOpen(false)}
                          aria-label="Close"
                        >
                          <X className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                      <ul className={styles.popoverList}>
                        {values.map((email) => (
                          <li key={email} className={styles.popoverListItem}>
                            {email}
                          </li>
                        ))}
                      </ul>
                      <p className={styles.popoverFooter}>
                        Showing {values.length} of {values.length} recipients.
                      </p>
                    </div>,
                    document.body,
                  )
                : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function ApplicantSelectionModal({
  isOpen,
  rows,
  search,
  loading,
  selectedApplicantId,
  onSearchChange,
  onSearch,
  onSelect,
  onCancel,
  onLink,
}: {
  isOpen: boolean
  rows: MailTraceApplicantSearchRow[]
  search: string
  loading: boolean
  selectedApplicantId: string
  onSearchChange: (value: string) => void
  onSearch: () => void
  onSelect: (value: string) => void
  onCancel: () => void
  onLink: () => void
}) {
  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Link Applicant"
      onClose={onCancel}
      maxWidth="max-w-800"
      bodyScrollOnly
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedApplicantId}
            onClick={onLink}
            className="inline-flex min-h-10 items-center justify-center rounded-xl bg-[rgb(21,151,255)] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgb(0,132,230)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            Link Applicant
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                onSearch()
              }
            }}
            className="min-h-11 flex-1 rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-800 focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15"
            placeholder="Search Applicant"
          />
          <button
            type="button"
            disabled={loading}
            onClick={onSearch}
            className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[rgb(21,151,255)] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgb(0,132,230)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Searching...' : 'Search Applicant'}
          </button>
        </div>

        {rows.length ? (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[680px] table-fixed text-left text-sm text-slate-700">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Applicant</th>
                  <th className="px-3 py-2">Application</th>
                  <th className="px-3 py-2">Resident Name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Phone Number</th>
                  <th className="px-3 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((row, index) => {
                  const applicantId = row.applicant_id
                  const applicantValue =
                    applicantId == null ? '' : String(applicantId)
                  return (
                    <tr key={`${applicantValue}-${index}`}>
                      <td className="px-3 py-2">{formatApplicantId(applicantId)}</td>
                      <td className="px-3 py-2">{formatApplicationId(row.application_id)}</td>
                      <td className="break-words px-3 py-2">
                        {String(row.resident_name ?? '-')}
                      </td>
                      <td className="break-words px-3 py-2">{String(row.email ?? '-')}</td>
                      <td className="px-3 py-2">{formatPhone(row.mobile_no)}</td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          disabled={!applicantValue}
                          checked={selectedApplicantId === applicantValue}
                          onChange={(event) =>
                            onSelect(event.target.checked ? applicantValue : '')
                          }
                          className="h-4 w-4 rounded border-slate-300 text-[rgb(21,151,255)] focus:ring-[rgb(21,151,255)]"
                          aria-label={`Select applicant ${formatApplicantId(applicantId)}`}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
            {loading ? 'Searching applicants...' : 'Search for an applicant to link.'}
          </div>
        )}
      </div>
    </OnScreenModal>
  )
}

export function MailLogDetailPage({ variant }: MailLogDetailMailLogDetailPageProps) {
  const { logId = '' } = useParams<{ logId: string }>()
  const { roleName } = useUserRole()
  useBreadcrumbPageTitle('Email Details')
  const [detail, setDetail] = useState<MailLogDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [toAddress, setToAddress] = useState(
    () => localStorage.getItem('searchEmail') ?? '',
  )
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [techOpen, setTechOpen] = useState(false)
  const [applicantModalOpen, setApplicantModalOpen] = useState(false)
  const [applicantSearch, setApplicantSearch] = useState('')
  const [applicantRows, setApplicantRows] = useState<MailTraceApplicantSearchRow[]>([])
  const [applicantSearchLoading, setApplicantSearchLoading] = useState(false)
  const [selectedApplicantId, setSelectedApplicantId] = useState('')
  const [linkConfirmOpen, setLinkConfirmOpen] = useState(false)
  const [linkingApplicant, setLinkingApplicant] = useState(false)

  useEffect(() => {
    if (!logId) return
    setLoading(true)
    void getMailLogDetail(variant, logId)
      .then(setDetail)
      .catch(showApiError)
      .finally(() => setLoading(false))
  }, [logId, variant])

  const view = useMemo(
    () => (detail ? detailListForVariant(variant, detail) : null),
    [detail, variant],
  )
  const attachments = useMemo(
    () =>
      (detail?.attachments ?? [])
        .map((attachment, index) =>
          normalizeAttachment(attachment, index, variant, logId),
        )
        .filter((attachment): attachment is MailLogDetailAttachmentLink => attachment != null),
    [detail?.attachments, logId, variant],
  )
  const html = useMemo(() => processHtmlLinks(view?.html ?? ''), [view?.html])
  const canResend = variant !== 'imap' && roleName !== 'ROLE_RENTER'
  const emailInvalid = Boolean(toAddress.trim()) && !EMAIL_RE.test(toAddress.trim())

  const sendMail = async () => {
    if (variant === 'imap' || !logId || emailInvalid) return
    setSending(true)
    try {
      const message = await resendMailLog(variant, logId, toAddress)
      showSuccess(message)
      setToAddress('')
      setSendConfirmOpen(false)
    } catch (err) {
      showApiError(err)
    } finally {
      setSending(false)
    }
  }

  const openApplicantLinkModal = () => {
    setApplicantSearch('')
    setApplicantRows([])
    setSelectedApplicantId('')
    setApplicantModalOpen(true)
  }

  const searchApplicants = async () => {
    setApplicantSearchLoading(true)
    try {
      const rows = await searchMailTraceApplicants(applicantSearch.trim())
      setApplicantRows(rows)
    } catch (err) {
      showApiError(err)
    } finally {
      setApplicantSearchLoading(false)
    }
  }

  const confirmApplicantLink = async () => {
    if (!logId || !selectedApplicantId) return
    setLinkingApplicant(true)
    try {
      await linkMailTraceApplicant(logId, selectedApplicantId)
      setDetail((prev) =>
        prev ? { ...prev, fk_applicant_id: selectedApplicantId } : prev,
      )
      setLinkConfirmOpen(false)
      setApplicantModalOpen(false)
      showSuccess('Applicant linked successfully.')
    } catch (err) {
      showApiError(err)
    } finally {
      setLinkingApplicant(false)
    }
  }

  const previousId = detail?.previous_mail_id
  const nextId = detail?.next_mail_id
  const showNav = Boolean(previousId || nextId || variant === 'legacy')
  const deliveryStatus =
    detail && variant === 'mailgun' && detail.event != null && String(detail.event).trim()
      ? String(detail.event)
      : null
  const deliveryTime =
    detail && variant === 'mailgun' ? formatDeliveryTime(detail.delay) : null

  const hasTechDelay =
    variant === 'mailgun' &&
    !deliveryTime &&
    detail != null &&
    detail.delay != null &&
    String(detail.delay).trim() !== ''
  const hasTechEvent =
    variant === 'mailgun' &&
    !deliveryStatus &&
    detail != null &&
    detail.event != null &&
    String(detail.event).trim() !== ''
  const hasTechDescription =
    variant === 'mailgun' &&
    detail != null &&
    detail.description != null &&
    String(detail.description).trim() !== ''
  const hasFailedMailLog = variant === 'mailgun' && Boolean(detail?.fk_mailgun_log_id)
  const hasTechnicalDetails = Boolean(
    view &&
      (view.bcc.length > 0 ||
        view.replyTo.length > 0 ||
        attachments.length > 0 ||
        hasTechDelay ||
        hasTechEvent ||
        hasTechDescription ||
        hasFailedMailLog ||
        variant === 'imap'),
  )

  return (
    <div data-testid={`${variant}-mail-detail-page`}>
      <PageShell
        title="Email Details"
        pageHeaderTestIdRoot={`${variant}-mail-detail-page-header`}
        contentClassName="!space-y-4 w-full max-w-none min-w-0 px-3 sm:px-5 lg:px-6"
        showBreadcrumbs
      >
        {loading ? (
          <div className={styles.loading}>
            <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-hidden />
          </div>
        ) : detail && view ? (
          <>
            <ConfirmDialog
              isOpen={sendConfirmOpen}
              title="Send Mail"
              message={`Send mail to ${toAddress.trim() || 'the original recipients'}?`}
              primaryLabel={sending ? 'Sending...' : 'Send'}
              onPrimary={sendMail}
              secondaryLabel="Cancel"
              onSecondary={() => setSendConfirmOpen(false)}
              onDismiss={() => setSendConfirmOpen(false)}
            />
            <ConfirmDialog
              isOpen={linkConfirmOpen}
              title="Link Applicant"
              message="Link this email to Selected Applicant"
              primaryLabel={linkingApplicant ? 'Linking...' : 'Continue'}
              onPrimary={confirmApplicantLink}
              secondaryLabel="Cancel"
              onSecondary={() => setLinkConfirmOpen(false)}
              onDismiss={() => setLinkConfirmOpen(false)}
            />
            <ApplicantSelectionModal
              isOpen={applicantModalOpen}
              rows={applicantRows}
              search={applicantSearch}
              loading={applicantSearchLoading}
              selectedApplicantId={selectedApplicantId}
              onSearchChange={setApplicantSearch}
              onSearch={searchApplicants}
              onSelect={setSelectedApplicantId}
              onCancel={() => setApplicantModalOpen(false)}
              onLink={() => setLinkConfirmOpen(true)}
            />

            <div className={styles.page}>
              <div className={styles.topBar}>
                <div className={styles.topBarStart}>
                  <Link to={reportPathForVariant(variant)} className={styles.backLink}>
                    <ArrowLeft className="h-4 w-4" aria-hidden />
                    <span className={styles.backLinkText}>
                      {variant === 'mailgun'
                        ? 'Mailgun Logs'
                        : variant === 'imap'
                          ? 'IMAP Inbox'
                          : 'Mail Logs'}
                    </span>
                  </Link>
                  <div className={styles.pageTitleBlock}>
                    <span className={styles.pageTitleIcon} aria-hidden>
                      <Mail className="h-4 w-4" />
                    </span>
                    <h2 className={styles.pageTitle}>Email Details</h2>
                  </div>
                </div>

                <div className={styles.toolbarActions}>
                  {canResend ? (
                    <div className={styles.sendGroup}>
                      <div className={styles.emailField}>
                        <div className={styles.emailInputWrap}>
                          <input
                            type="text"
                            inputMode="email"
                            autoComplete="email"
                            value={toAddress}
                            onChange={(e) => setToAddress(e.target.value)}
                            placeholder="To address (optional)"
                            className={cn(
                              styles.emailInput,
                              emailInvalid && styles.emailInputInvalid,
                            )}
                            aria-invalid={emailInvalid}
                            aria-describedby={
                              emailInvalid ? 'mail-resend-email-error' : undefined
                            }
                            aria-label="Recipient email address"
                          />
                        </div>
                        {emailInvalid ? (
                          <p id="mail-resend-email-error" className={styles.emailError}>
                            Enter a valid email address.
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        disabled={sending || emailInvalid}
                        onClick={() => setSendConfirmOpen(true)}
                        className={styles.sendButton}
                        aria-label={sending ? 'Sending email' : 'Send Email'}
                        title="Send Email"
                      >
                        {sending ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        ) : (
                          <Mail className="h-4 w-4" aria-hidden />
                        )}
                        <span className={styles.sendButtonLabel}>Send Email</span>
                      </button>
                    </div>
                  ) : null}

                  {showNav ? (
                    <div className={styles.navGroup} role="group" aria-label="Mail navigation">
                      {previousId ? (
                        <Link
                          to={detailPathForVariant(variant, previousId)}
                          className={styles.navButton}
                        >
                          <ChevronLeft className="h-4 w-4" aria-hidden />
                          Previous
                        </Link>
                      ) : (
                        <span className={styles.navButtonDisabled}>
                          <ChevronLeft className="h-4 w-4" aria-hidden />
                          Previous
                        </span>
                      )}
                      {nextId ? (
                        <Link
                          to={detailPathForVariant(variant, nextId)}
                          className={styles.navButton}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" aria-hidden />
                        </Link>
                      ) : (
                        <span className={styles.navButtonDisabled}>
                          Next
                          <ChevronRight className="h-4 w-4" aria-hidden />
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className={styles.body}>
                <aside className={styles.sideColumn}>
                  <div className={styles.card}>
                    <div className={styles.cardHeader}>
                      <Info className={styles.cardHeaderIcon} aria-hidden />
                      <h3 className={styles.cardHeaderTitle}>Email Information</h3>
                    </div>
                    <div className={styles.cardBody}>
                      <AddressField
                        label="From"
                        icon={<Mail className="h-3.5 w-3.5" aria-hidden />}
                        badgeClass={styles.iconBadgeFrom}
                        values={view.from}
                      />
                      <AddressField
                        label="To"
                        icon={<UserPlus className="h-3.5 w-3.5" aria-hidden />}
                        badgeClass={styles.iconBadgeTo}
                        values={view.to}
                      />
                      <AddressField
                        label="Cc"
                        icon={<UserPlus className="h-3.5 w-3.5" aria-hidden />}
                        badgeClass={styles.iconBadgeCc}
                        values={view.cc}
                      />
                      {view.date ? (
                        <div className={styles.infoRow}>
                          <span
                            className={cn(styles.iconBadge, styles.iconBadgeDate)}
                            aria-hidden
                          >
                            <Calendar className="h-3.5 w-3.5" aria-hidden />
                          </span>
                          <div className={styles.infoContent}>
                            <p className={styles.infoLabel}>Date</p>
                            <p className={styles.infoValue}>{formatDateTime(view.date)}</p>
                          </div>
                        </div>
                      ) : null}
                      {deliveryStatus ? (
                        <div className={styles.infoRow}>
                          <span
                            className={cn(styles.iconBadge, styles.iconBadgeStatus)}
                            aria-hidden
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                          </span>
                          <div className={styles.infoContent}>
                            <p className={styles.infoLabel}>Delivery Status</p>
                            <span
                              className={cn(
                                styles.statusBadge,
                                deliveryStatusTone(deliveryStatus),
                              )}
                            >
                              {deliveryStatus}
                            </span>
                          </div>
                        </div>
                      ) : null}
                      {deliveryTime ? (
                        <div className={styles.infoRow}>
                          <span
                            className={cn(styles.iconBadge, styles.iconBadgeDelay)}
                            aria-hidden
                          >
                            <Clock className="h-3.5 w-3.5" aria-hidden />
                          </span>
                          <div className={styles.infoContent}>
                            <p className={styles.infoLabel}>Delivery Time</p>
                            <p className={styles.infoValue}>{deliveryTime}</p>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {hasTechnicalDetails ? (
                      <div className={styles.techDetails}>
                        <button
                          type="button"
                          className={styles.techToggle}
                          onClick={() => setTechOpen((v) => !v)}
                          aria-expanded={techOpen}
                        >
                          Technical Details
                          {techOpen ? (
                            <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                          ) : (
                            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                          )}
                        </button>
                        {techOpen ? (
                          <div className={styles.techBody}>
                            {view.bcc.length ? (
                              <p className={styles.techStat}>
                                <strong>Bcc: </strong>
                                {view.bcc.join(', ')}
                              </p>
                            ) : null}
                            {view.replyTo.length ? (
                              <p className={styles.techStat}>
                                <strong>Reply-To: </strong>
                                {view.replyTo.join(', ')}
                              </p>
                            ) : null}
                            {variant === 'mailgun' ? (
                              <>
                                {hasTechDelay ? (
                                  <p className={styles.techStat}>
                                    <strong>Delay: </strong>
                                    {Number(detail.delay).toFixed(2)} Second
                                  </p>
                                ) : null}
                                {hasTechEvent ? (
                                  <p className={styles.techStat}>
                                    <strong>Event: </strong>
                                    {String(detail.event)}
                                  </p>
                                ) : null}
                                {hasTechDescription ? (
                                  <p className={styles.techStat}>
                                    <strong>Description: </strong>
                                    {String(detail.description)}
                                  </p>
                                ) : null}
                                {hasFailedMailLog ? (
                                  <Link
                                    to={detailPathForVariant(
                                      'mailgun',
                                      String(detail.fk_mailgun_log_id),
                                    )}
                                    className={styles.techLink}
                                  >
                                    Failed Mail Log
                                  </Link>
                                ) : null}
                              </>
                            ) : null}
                            {variant === 'imap' ? (
                              detail.fk_applicant_id ? (
                                <Link
                                  to={ROUTE_BUILDERS.DASHBOARD_APPLICATION_DETAIL(
                                    String(detail.fk_applicant_id),
                                  )}
                                  className={styles.techLink}
                                >
                                  Applicant: {String(detail.fk_applicant_id)}
                                </Link>
                              ) : (
                                <button
                                  type="button"
                                  onClick={openApplicantLinkModal}
                                  className={styles.techLink}
                                >
                                  Link To Applicant
                                </button>
                              )
                            ) : null}
                            {attachments.length ? (
                              <div>
                                <p className={styles.techStat}>
                                  <strong>Attachments</strong>
                                </p>
                                <ul className={styles.attachmentsList}>
                                  {attachments.map((attachment) => (
                                    <li key={`${attachment.name}-${attachment.href}`}>
                                      <a
                                        href={attachment.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download={attachment.name}
                                        className={styles.attachmentLink}
                                      >
                                        <span className={styles.attachmentNameWrap}>
                                          <Paperclip
                                            className="h-4 w-4 shrink-0"
                                            aria-hidden
                                          />
                                          <span className={styles.attachmentName}>
                                            {attachment.name}
                                          </span>
                                        </span>
                                        {attachment.size ? (
                                          <span className={styles.attachmentMeta}>
                                            {attachment.size}
                                          </span>
                                        ) : null}
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </aside>

                <section className={styles.previewColumn}>
                  <div className={styles.subjectBar}>
                    <span className={styles.subjectIcon} aria-hidden>
                      <FileText className="h-4 w-4" />
                    </span>
                    <div className={styles.subjectText}>
                      <p className={styles.subjectLabel}>Subject</p>
                      <p className={styles.subjectValue}>{view.title || '—'}</p>
                    </div>
                  </div>
                  <div className={cn(styles.card, styles.previewCard)}>
                    <div className={styles.previewBody}>
                      {html ? (
                        <div className={styles.previewSurface}>
                          <iframe
                            title="Mail content"
                            srcDoc={html}
                            sandbox="allow-popups allow-popups-to-escape-sandbox"
                            className={styles.previewFrame}
                          />
                        </div>
                      ) : (
                        <p className={styles.emptyBody}>No message body available.</p>
                      )}
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </>
        ) : (
          <p className={styles.notFound}>Mail log not found.</p>
        )}
      </PageShell>
    </div>
  )
}
